"use client"

import type React from "react"

import { useState, useCallback, useEffect, useMemo, Suspense, useRef } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
  Plus,
  Trash2,
  ArrowLeft,
  LinkIcon,
  Hash,
  MapPin,
  Target,
  TrendingUp,
  Shield,
  Users,
  AlertCircle,
  X,
  CalendarIcon,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const QUARTERS = [
  { label: "Q1", months: "Jan - Mar", startMonth: 0 },
  { label: "Q2", months: "Apr - Jun", startMonth: 3 },
  { label: "Q3", months: "Jul - Sep", startMonth: 6 },
  { label: "Q4", months: "Oct - Dec", startMonth: 9 },
]

interface Category {
  id: number
  text: string
}

interface Client {
  id: number
  company_name?: string
  first_name?: string
  last_name?: string
  email?: string
  businesses?: Business[]
}

interface Business {
  id: number
  name: string
  website?: string
  defaultMetrics?: {
    da: number
    dr: number
    traffic: string
  }
  wantsList?: Category[]
  avoidList?: Category[]
}

interface LinkInfoRow {
  id: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zipcode: string
  targetUrl: string
  anchor: string
  linkCount: string
  da: string
  dr: string
  traffic: string
  spam: string
}

interface CreateOrderFormProps {
  isOpen?: boolean
  onClose?: () => void
  onSave?: (orderData: any) => Promise<void>
  initialClientId?: string
  initialBusinessId?: string
  asModal?: boolean
}

function CreateOrderFormContent({ 
  isOpen = true, 
  onClose, 
  onSave,
  initialClientId,
  initialBusinessId,
  asModal = false 
}: CreateOrderFormProps) {
  const router = useRouter()
  const { toast } = useToast()

  const clientIdParam = initialClientId || ""
  const businessIdParam = initialBusinessId || ""

  const [clientId, setClientId] = useState(clientIdParam || "")
  const [businessId, setBusinessId] = useState(businessIdParam || "")
  const [clients, setClients] = useState<Client[]>([])
  const clientsRef = useRef<Client[]>([])
  const [initialClients, setInitialClients] = useState<Client[]>([])
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [clientsLoaded, setClientsLoaded] = useState(false)
  const [clientSearchTerm, setClientSearchTerm] = useState("")
  const [businessSearchTerm, setBusinessSearchTerm] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false)
  const [clientAbortController, setClientAbortController] = useState<AbortController | null>(null)
  const [clientError, setClientError] = useState(false)
  const [totalBudgetError, setTotalBudgetError] = useState(false)
  const [totalLinksNeededError, setTotalLinksNeededError] = useState(false)
  const [dueDateError, setDueDateError] = useState(false)
  const [linkInfoRowsErrors, setLinkInfoRowsErrors] = useState<Record<string, Set<string>>>({})
  const [opportunityTypesError, setOpportunityTypesError] = useState(false)
  const [wantsError, setWantsError] = useState(false)
  const [avoidError, setAvoidError] = useState(false)
  const [anchorRequirementError, setAnchorRequirementError] = useState(false)
  const [recurringMonthlyError, setRecurringMonthlyError] = useState(false)
  const [flexibleOnMetricsError, setFlexibleOnMetricsError] = useState(false)
  const [flexibleOnWantsAvoidsError, setFlexibleOnWantsAvoidsError] = useState(false)
  const [recurringFrequency, setRecurringFrequency] = useState<"Monthly" | "Quarterly" | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [endDateError, setEndDateError] = useState(false)
  const [selectedEndMonth, setSelectedEndMonth] = useState<number | null>(null)
  const [selectedEndYear, setSelectedEndYear] = useState<number | null>(null)
  const [selectedEndQuarter, setSelectedEndQuarter] = useState<number | null>(null)
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear())
  const [showPicker, setShowPicker] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  // Refs for focusing on first error field
  const clientInputRef = useRef<HTMLInputElement>(null)
  const totalBudgetInputRef = useRef<HTMLInputElement>(null)
  const totalLinksNeededInputRef = useRef<HTMLInputElement>(null)
  const dueDateButtonRef = useRef<HTMLButtonElement>(null)
  const linkInfoSectionRef = useRef<HTMLDivElement>(null)
  const anchorRequirementRef = useRef<HTMLDivElement>(null)
  const opportunityTypesRef = useRef<HTMLDivElement>(null)
  const wantsRef = useRef<HTMLDivElement>(null)
  const avoidRef = useRef<HTMLDivElement>(null)
  const recurringMonthlyRef = useRef<HTMLDivElement>(null)
  const flexibleOnMetricsRef = useRef<HTMLDivElement>(null)
  const flexibleOnWantsAvoidsRef = useRef<HTMLDivElement>(null)
  const endDateButtonRef = useRef<HTMLButtonElement>(null)

  const [linkInfoRows, setLinkInfoRows] = useState<LinkInfoRow[]>([
    {
      id: Date.now().toString(),
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipcode: "",
      targetUrl: "",
      anchor: "",
      linkCount: "",
      da: "",
      dr: "",
      traffic: "",
      spam: "",
    },
  ])

  // Global metrics state (not per row)
  const [globalMetrics, setGlobalMetrics] = useState({
    da: "",
    dr: "",
    traffic: "",
    spam: "",
  })

  const [anchorRequirement, setAnchorRequirement] = useState<"preferred" | "required" | undefined>(undefined)

  const [wantsList, setWantsList] = useState<Category[]>([])
  const [avoidList, setAvoidList] = useState<Category[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [initialCategories, setInitialCategories] = useState<Category[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [wantsSearchTerm, setWantsSearchTerm] = useState("")
  const [avoidSearchTerm, setAvoidSearchTerm] = useState("")
  const [showWantsDropdown, setShowWantsDropdown] = useState(false)
  const [showAvoidDropdown, setShowAvoidDropdown] = useState(false)
  const [categoriesLoaded, setCategoriesLoaded] = useState(false)
  const [isAllCategoriesSelected, setIsAllCategoriesSelected] = useState(false)
  const [allCategories, setAllCategories] = useState<Category[]>([])
  const [loadingAllCategories, setLoadingAllCategories] = useState(false)

  const [totalBudget, setTotalBudget] = useState("")
  const [totalLinksNeeded, setTotalLinksNeeded] = useState("")
  const [zsOppFee, setZsOppFee] = useState("150")
  const [dueDate, setDueDate] = useState<Date | undefined>(() => {
    const threeWeeksFromNow = new Date()
    threeWeeksFromNow.setDate(threeWeeksFromNow.getDate() + 21)
    return threeWeeksFromNow
  })
  const [recurringMonthly, setRecurringMonthly] = useState<"yes" | "no" | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [otherRequirements, setOtherRequirements] = useState("")
  const [flexibleOnMetrics, setFlexibleOnMetrics] = useState<"yes" | "no" | undefined>(undefined)
  const [flexiblePerMetric, setFlexiblePerMetric] = useState({
    da: false,
    dr: false,
    traffic: false,
    spam: false,
  })
  const [flexibleOnWantsAvoids, setFlexibleOnWantsAvoids] = useState<"yes" | "no" | undefined>(undefined)

  const [opportunityTypes, setOpportunityTypes] = useState({
    generalDonation: true,
    event: true,
    organizationMemberships: true,
    chamberOfCommerceMemberships: false,
  })

  const totalLinksPerUrl = useMemo(() => {
    return linkInfoRows.reduce((sum, row) => {
      const linkCount = Number.parseInt(row.linkCount) || 0
      return sum + linkCount
    }, 0)
  }, [linkInfoRows])

  const totalLinksNeededNum = Number.parseInt(totalLinksNeeded) || 0
  const remainingLinks = totalLinksNeededNum - totalLinksPerUrl
  const isOverLimit = totalLinksNeededNum > 0 && totalLinksPerUrl > totalLinksNeededNum
  const isUnderLimit = totalLinksNeededNum > 0 && totalLinksPerUrl < totalLinksNeededNum
  const isNearLimit = totalLinksNeededNum > 0 && remainingLinks <= 2 && remainingLinks > 0


  const handleOpportunityTypeChange = (type: keyof typeof opportunityTypes) => {
    setOpportunityTypes((prev) => ({
      ...prev,
      [type]: !prev[type],
    }))
    if (opportunityTypesError) {
      setOpportunityTypesError(false)
    }
  }

  const handleSelectAllOpportunityTypes = () => {
    const allSelected = Object.values(opportunityTypes).every((value) => value)
    const newState = {
      generalDonation: !allSelected,
      event: !allSelected,
      organizationMemberships: !allSelected,
      chamberOfCommerceMemberships: !allSelected,
    }
    setOpportunityTypes(newState)
  }

  const totalZsFee = Number(zsOppFee || 150) * (Number(totalLinksNeeded) || 0)
  
  // Cost per Sponsorship (how much will each sponsorship cost for the client)
  const calculatedCostPerSponsorship = 
    totalBudget && totalLinksNeeded && Number(totalLinksNeeded) > 0
      ? Number(totalBudget) / Number(totalLinksNeeded)
      : 0
  const costPerSponsorship = calculatedCostPerSponsorship > 0 ? calculatedCostPerSponsorship.toFixed(2) : "0.00"
  
  // Net Budget per Sponsorship (how much budget there is specifically for the sponsorship)
  const calculatedPricePerSponsorship = 
    totalBudget && totalLinksNeeded && Number(totalLinksNeeded) > 0
      ? (Number(totalBudget) / Number(totalLinksNeeded)) - Number(zsOppFee || 150)
      : 0
  const pricePerSponsorship = calculatedPricePerSponsorship > 0 ? calculatedPricePerSponsorship.toFixed(2) : "0.00"

  // Handle clicks outside picker
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Handle frequency change
  const handleFrequencyChange = (value: string) => {
    setRecurringFrequency(value as "Monthly" | "Quarterly")
    setSelectedEndMonth(null)
    setSelectedEndYear(null)
    setSelectedEndQuarter(null)
    setEndDate(undefined)
    setShowPicker(false)
  }

  // Handle month selection
  const handleSelectMonth = (monthIndex: number) => {
    setSelectedEndMonth(monthIndex)
    setSelectedEndYear(pickerYear)
    // Calculate end date as last day of selected month
    const endDateForMonth = new Date(pickerYear, monthIndex + 1, 0)
    setEndDate(endDateForMonth)
    setShowPicker(false)
  }

  // Handle quarter selection
  const handleSelectQuarter = (quarterIndex: number) => {
    setSelectedEndQuarter(quarterIndex)
    setSelectedEndYear(pickerYear)
    // Calculate end date as last day of last month in quarter
    const endMonthOfQuarter = QUARTERS[quarterIndex].startMonth + 2
    const endDateForQuarter = new Date(pickerYear, endMonthOfQuarter + 1, 0)
    setEndDate(endDateForQuarter)
    setShowPicker(false)
  }

  // Get end date display text
  const getEndDateDisplay = () => {
    if (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) {
      return `${MONTHS_FULL[selectedEndMonth]} ${selectedEndYear}`
    }
    if (recurringFrequency === "Quarterly" && selectedEndQuarter !== null && selectedEndYear !== null) {
      return `${QUARTERS[selectedEndQuarter].label} ${selectedEndYear} (${QUARTERS[selectedEndQuarter].months})`
    }
    return ""
  }

  // Get start of next quarter (Apr=3, Jul=6, Oct=9, Jan=0 next year)
  const getNextQuarterStart = (date: Date): Date => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const quarterIndex = Math.floor(month / 3) + 1
    const nextMonth = quarterIndex * 3
    if (nextMonth >= 12) {
      return new Date(year + 1, 0, 1)
    }
    return new Date(year, nextMonth, 1)
  }

  const recurringSeries = useMemo(() => {
    if (recurringMonthly !== "yes" || !recurringFrequency) return []

    let endDateForSeries: Date | null = null
    if (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) {
      endDateForSeries = new Date(selectedEndYear, selectedEndMonth + 1, 0)
    } else if (recurringFrequency === "Quarterly" && selectedEndQuarter !== null && selectedEndYear !== null) {
      const endMonthOfQuarter = QUARTERS[selectedEndQuarter].startMonth + 2
      endDateForSeries = new Date(selectedEndYear, endMonthOfQuarter + 1, 0)
    }

    if (!endDateForSeries) return []

    const start = new Date()
    start.setHours(0, 0, 0, 0)
    if (Number.isNaN(start.getTime())) return []

    const series: { number: number; month: string; startDate: string }[] = []

    if (recurringFrequency === "Monthly") {
      let current = new Date(start)
      let count = 1
      while (current <= endDateForSeries) {
        const monthName = current.toLocaleDateString("en-US", { month: "long", year: "numeric" })
        const formattedDate = current.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
        series.push({ number: count, month: monthName, startDate: formattedDate })
        current.setMonth(current.getMonth() + 1)
        count++
      }
    } else {
      let current = new Date(start)
      let count = 1
      while (current <= endDateForSeries) {
        const monthName = current.toLocaleDateString("en-US", { month: "long", year: "numeric" })
        const formattedDate = current.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
        series.push({ number: count, month: monthName, startDate: formattedDate })
        current = getNextQuarterStart(current)
        count++
      }
    }

    return series
  }, [recurringMonthly, recurringFrequency, selectedEndMonth, selectedEndYear, selectedEndQuarter])

  // Calculate total number of orders based on start date, end date, and frequency
  const calculateTotalOrders = (startDate: Date, endDate: Date, frequency: "Monthly" | "Quarterly"): number => {
    if (!startDate || !endDate || endDate < startDate) return 0

    if (frequency === "Monthly") {
      let count = 0
      const current = new Date(startDate)
      while (current <= endDate) {
        count++
        current.setMonth(current.getMonth() + 1)
      }
      return count
    } else {
      // Quarterly
      let count = 0
      const current = new Date(startDate)
      while (current <= endDate) {
        count++
        current.setMonth(current.getMonth() + 3)
      }
      return count
    }
  }

  // Validate if all required fields are filled
  const isFormValid = useMemo(() => {
    // Basic required fields
    if (
      !clientId.trim() ||
      !totalBudget.trim() ||
      !totalLinksNeeded.trim() ||
      !dueDate
    ) {
      return false
    }

    // TODO: Temporarily disabled - Link Information validation
    // Validate link info rows - all required fields must be filled
    // const hasValidLinkInfoRows = linkInfoRows.length > 0 && linkInfoRows.every((row) => {
    //   return (
    //     row.targetUrl.trim() !== "" &&
    //     row.anchor.trim() !== "" &&
    //     row.location.trim() !== "" &&
    //     row.linkCount.trim() !== "" &&
    //     row.da.trim() !== "" &&
    //     row.dr.trim() !== "" &&
    //     row.traffic.trim() !== "" &&
    //     row.spam.trim() !== ""
    //   )
    // })

    // if (!hasValidLinkInfoRows) {
    //   return false
    // }

    // At least one opportunity type must be selected
    const hasOpportunityType = Object.values(opportunityTypes).some((value) => value)
    if (!hasOpportunityType) {
      return false
    }

    // At least one want category must be selected (either individual or all categories)
    if (wantsList.length === 0 && !isAllCategoriesSelected) {
      return false
    }

    // At least one avoid category must be selected
    if (avoidList.length === 0) {
      return false
    }

    // Validate radio button selections
    if (!anchorRequirement) {
      return false
    }

    if (!recurringMonthly) {
      return false
    }

    // If recurring is enabled, validate frequency and end date
    if (recurringMonthly === "yes") {
      if (!recurringFrequency) {
        return false
      }
      if (!endDate) {
        return false
      }
    }

    if (!flexibleOnMetrics) {
      return false
    }

    if (!flexibleOnWantsAvoids) {
      return false
    }

    return true
  }, [
    clientId,
    totalBudget,
    totalLinksNeeded,
    dueDate,
    opportunityTypes,
    wantsList,
    avoidList,
    anchorRequirement,
    recurringMonthly,
    recurringFrequency,
    endDate,
    flexibleOnMetrics,
    flexibleOnWantsAvoids,
  ])

  const fetchClients = async (searchTerm = "", abortController?: AbortController) => {
    try {
      setLoadingClients(true)

      const url = searchTerm
        ? `${CRM_API_BASE}/client/?q=${encodeURIComponent(searchTerm)}&limit=20`
        : `${CRM_API_BASE}/client/?limit=20`

      const response = await httpJson<{ results?: Client[] }>(url, {
        signal: abortController?.signal,
      })

      if (response.results) {
        if (searchTerm) {
          setClients(response.results)
        } else {
          setInitialClients((prevInitialClients) => {
            const newClients = response.results || []
            // If we have an initialClientId that's not in the results, ensure it's preserved
            if (initialClientId && initialClientId.trim() !== "") {
              const initialClientExists = newClients.some(client => client.id.toString() === initialClientId)
              if (!initialClientExists) {
                const existingInitialClient = prevInitialClients.find(client => client.id.toString() === initialClientId)
                if (existingInitialClient) {
                  return [existingInitialClient, ...newClients]
                }
              }
            }
            return newClients
          })
          setClients((prevClients) => {
            const newClients = response.results || []
            // Preserve the selected client if it exists
            if (clientId && clientId.trim() !== "") {
              const selectedClientExists = newClients.some(client => client.id.toString() === clientId)
              if (!selectedClientExists) {
                const existingSelectedClient = prevClients.find(client => client.id.toString() === clientId)
                if (existingSelectedClient) {
                  return [existingSelectedClient, ...newClients]
                }
              }
            }
            return newClients
          })
        }
        setClientsLoaded(true)
      } else {
        if (!searchTerm) {
          setInitialClients([])
        }
        setClients([])
      }
    } catch (error) {
      if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) {
        return
      }
      console.error("Error fetching clients:", error)
      setClients([])
    } finally {
      setLoadingClients(false)
      if (abortController) {
        setClientAbortController(null)
      }
    }
  }

  const fetchBusinesses = useCallback(async (clientId?: string) => {
    try {
      if (!clientId) {
        setBusinesses([])
        return
      }

      const currentClients = clientsRef.current
      const selectedClient = currentClients.find((client) => client.id.toString() === clientId)

      if (selectedClient && selectedClient.businesses && selectedClient.businesses.length > 0) {
        setBusinesses(selectedClient.businesses)
      } else {
        // If client doesn't have businesses loaded, fetch the full client data
        try {
          console.log("[CreateOrderForm] Fetching client businesses:", clientId)
          const clientResponse = await httpJson<Client>(`${CRM_API_BASE}/client/${clientId}/?include_archived=true`)
          if (clientResponse.businesses && clientResponse.businesses.length > 0) {
            setBusinesses(clientResponse.businesses)
            // Update the client in the lists with businesses
            setClients((prevClients) => {
              return prevClients.map((client) => 
                client.id.toString() === clientId 
                  ? { ...client, businesses: clientResponse.businesses }
                  : client
              )
            })
            setInitialClients((prevInitialClients) => {
              return prevInitialClients.map((client) => 
                client.id.toString() === clientId 
                  ? { ...client, businesses: clientResponse.businesses }
                  : client
              )
            })
          } else {
            setBusinesses([])
          }
        } catch (fetchError) {
          console.error("Error fetching client businesses:", fetchError)
          setBusinesses([])
        }
      }
    } catch (error) {
      console.error("Error getting businesses from client:", error)
      setBusinesses([])
    }
  }, [])

  useEffect(() => {
    if (asModal && !isOpen) {
      setClientId("")
      setBusinessId("")
      setClients([])
      setInitialClients([])
      setBusinesses([])
      setClientsLoaded(false)
      setCategoriesLoaded(false)
      setClientSearchTerm("")
      setBusinessSearchTerm("")
      setShowClientDropdown(false)
      setShowBusinessDropdown(false)
      setClientError(false)
      setTotalBudgetError(false)
      setTotalLinksNeededError(false)
      setDueDateError(false)
      setLinkInfoRowsErrors({})
      setOpportunityTypesError(false)
      setWantsError(false)
      setAvoidError(false)
      setAnchorRequirementError(false)
      setRecurringMonthlyError(false)
      setFlexibleOnMetricsError(false)
      setFlexibleOnWantsAvoidsError(false)
      setEndDateError(false)
      setLinkInfoRows([{
        id: Date.now().toString(),
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipcode: "",
        targetUrl: "",
        anchor: "",
        linkCount: "",
        da: "",
        dr: "",
        traffic: "",
        spam: "",
      }])
      setGlobalMetrics({ da: "", dr: "", traffic: "", spam: "" })
      setAnchorRequirement(undefined)
      setWantsList([])
      setAvoidList([])
      setWantsSearchTerm("")
      setAvoidSearchTerm("")
      setShowWantsDropdown(false)
      setShowAvoidDropdown(false)
      setTotalBudget("")
      setTotalLinksNeeded("")
      setZsOppFee("150")
      const threeWeeksFromNow = new Date()
      threeWeeksFromNow.setDate(threeWeeksFromNow.getDate() + 21)
      setDueDate(threeWeeksFromNow)
      setRecurringMonthly(undefined)
      setRecurringFrequency(undefined)
      setEndDate(undefined)
      setSelectedEndMonth(null)
      setSelectedEndYear(null)
      setSelectedEndQuarter(null)
      setPickerYear(new Date().getFullYear())
      setShowPicker(false)
      setOtherRequirements("")
      setFlexibleOnMetrics(undefined)
      setFlexiblePerMetric({ da: false, dr: false, traffic: false, spam: false })
      setFlexibleOnWantsAvoids(undefined)
      setOpportunityTypes({
        generalDonation: true,
        event: true,
        organizationMemberships: true,
        chamberOfCommerceMemberships: false,
      })
      if (clientAbortController) {
        clientAbortController.abort()
        setClientAbortController(null)
      }
    }
  }, [asModal, isOpen])

  useEffect(() => {
    if (isOpen) {
      fetchClients()
      fetchCategories()
    }
  }, [isOpen]) // Only fetch when modal is opened

  // Sync clientId when initialClientId changes (for both modal and page modes)
  useEffect(() => {
    if (initialClientId && initialClientId.trim() !== "" && clientId !== initialClientId) {
      setClientId(initialClientId)
    }
  }, [initialClientId])

  // Sync businessId when initialBusinessId changes (for both modal and page modes)
  useEffect(() => {
    if (initialBusinessId && initialBusinessId.trim() !== "" && clientId && businessId !== initialBusinessId) {
      setBusinessId(initialBusinessId)
    }
  }, [initialBusinessId, clientId])

  // When modal/page opens, ensure initial values are set
  useEffect(() => {
    if (isOpen || !asModal) {
      if (initialClientId && initialClientId.trim() !== "") {
        setClientId(initialClientId)
      }
      if (initialBusinessId && initialBusinessId.trim() !== "" && initialClientId) {
        setBusinessId(initialBusinessId)
      }
    }
  }, [isOpen, asModal, initialClientId, initialBusinessId])

  // Fetch specific client when initialClientId is provided and modal/page is open
  useEffect(() => {
    if (!initialClientId || initialClientId.trim() === "" || (!isOpen && asModal)) {
      return
    }
    
    // Use functional updates to check current state without adding dependencies
    setInitialClients(prevInitialClients => {
      setClients(prevClients => {
        // Check if client already exists in either list
        const clientExists = prevInitialClients.some(client => client.id.toString() === initialClientId) ||
                            prevClients.some(client => client.id.toString() === initialClientId)
        
        if (clientExists) {
          // Client already exists, ensure clientId is set
          setClientId(prevClientId => prevClientId !== initialClientId ? initialClientId : prevClientId)
          return prevClients
        }
        
        // Client doesn't exist, fetch it
        const fetchInitialClient = async () => {
          try {
            console.log("[CreateOrderForm] Fetching client:", initialClientId)
            const clientResponse = await httpJson<Client>(`${CRM_API_BASE}/client/${initialClientId}/?include_archived=true`)
            console.log("[CreateOrderForm] Client fetched:", clientResponse)
            
            // Add the client to both initialClients and clients using functional updates
            setInitialClients(prev => {
              const exists = prev.some(client => client.id.toString() === initialClientId)
              if (!exists) {
                return [clientResponse, ...prev]
              }
              return prev
            })
            
            setClients(prev => {
              const exists = prev.some(client => client.id.toString() === initialClientId)
              if (!exists) {
                return [clientResponse, ...prev]
              }
              return prev
            })
            
            // Ensure clientId is set to the initialClientId (this will trigger selectedClient to update)
            setClientId(initialClientId)
            console.log("[CreateOrderForm] ClientId set to:", initialClientId)
          } catch (error) {
            console.error("Error fetching initial client:", error)
            // Don't show error to user, just log it - the client might not exist or user might not have access
          }
        }
        
        fetchInitialClient()
        return prevClients
      })
      return prevInitialClients
    })
  }, [initialClientId, isOpen, asModal]) // Only fetch when modal/page is open

  // When businesses are loaded and we have initialBusinessId, set it
  useEffect(() => {
    if (initialBusinessId && initialBusinessId.trim() !== "" && businesses.length > 0) {
      const businessExists = businesses.some(business => business.id.toString() === initialBusinessId)
      if (businessExists && businessId !== initialBusinessId) {
        setBusinessId(initialBusinessId)
        console.log("[CreateOrderForm] BusinessId set after businesses loaded:", initialBusinessId)
      }
    }
  }, [businesses, initialBusinessId, businessId])

  // Ensure clientId remains set after initial clients load
  useEffect(() => {
    if (initialClientId && initialClientId.trim() !== "" && clientsLoaded) {
      const clientExists = initialClients.some(client => client.id.toString() === initialClientId) ||
                          clients.some(client => client.id.toString() === initialClientId)
      if (clientExists && clientId !== initialClientId) {
        setClientId(initialClientId)
      }
    }
  }, [initialClients, clients, initialClientId, clientId, clientsLoaded])

  useEffect(() => {
    if (initialBusinessId && initialBusinessId.trim() !== "" && clientId && businessId !== initialBusinessId) {
      const businessExists = businesses.some(business => business.id.toString() === initialBusinessId)
      if (businessExists) {
        setBusinessId(initialBusinessId)
      }
    }
  }, [businesses, initialBusinessId, clientId, businessId])

  useEffect(() => {
    clientsRef.current = clients
  }, [clients])

  useEffect(() => {
    if (clientId) {
      fetchBusinesses(clientId)
    } else {
      setBusinesses([])
    }
  }, [clientId, fetchBusinesses])

  const selectedClient = useMemo(() => {
    const foundInInitial = initialClients.find((client) => client.id.toString() === clientId)
    const foundInClients = clients.find((client) => client.id.toString() === clientId)
    return foundInInitial || foundInClients
  }, [clientId, initialClients, clients])

  const selectedBusiness = businesses.find((business) => business.id.toString() === businessId)

  useEffect(() => {
    if (!clientSearchTerm.trim() || clientSearchTerm.trim().length < 2) {
      let clientsToShow = [...initialClients]
      
      // Always include the selected client if clientId is set
      if (clientId && clientId.trim() !== "") {
        const currentClients = clientsRef.current
        const selectedClient = currentClients.find((client) => client.id.toString() === clientId)
        if (selectedClient && !clientsToShow.some((client) => client.id.toString() === clientId)) {
          clientsToShow = [selectedClient, ...clientsToShow]
        }
      }
      
      // Also include initialClientId if it's different from clientId
      if (initialClientId && initialClientId.trim() !== "" && initialClientId !== clientId) {
        const currentClients = clientsRef.current
        const initialClient = currentClients.find((client) => client.id.toString() === initialClientId)
        if (initialClient && !clientsToShow.some((client) => client.id.toString() === initialClientId)) {
          clientsToShow = [initialClient, ...clientsToShow]
        }
      }

      // Only update if the list actually changed to avoid infinite loops
      setClients((prevClients) => {
        const prevIds = prevClients.map(c => c.id.toString()).sort().join(',')
        const newIds = clientsToShow.map(c => c.id.toString()).sort().join(',')
        if (prevIds !== newIds) {
          return clientsToShow
        }
        return prevClients
      })
      setLoadingClients(false)
      if (clientAbortController) {
        clientAbortController.abort()
        setClientAbortController(null)
      }
      return
    }

    const timer = setTimeout(() => {
      if (showClientDropdown && clientSearchTerm.trim().length >= 2) {
        if (clientAbortController) {
          clientAbortController.abort()
        }
        const newAbortController = new AbortController()
        setClientAbortController(newAbortController)
        fetchClients(clientSearchTerm, newAbortController)
      }
    }, 800)
    return () => clearTimeout(timer)
  }, [clientSearchTerm, showClientDropdown, initialClients, clientId])

  const filteredBusinesses = useMemo(() => {
    if (!businessSearchTerm.trim()) return businesses
    return businesses.filter((business) => business.name.toLowerCase().includes(businessSearchTerm.toLowerCase()))
  }, [businesses, businessSearchTerm])

  const fetchCategories = async (searchTerm = "") => {
    try {
      setLoadingCategories(true)

      const url = searchTerm
        ? `${CRM_API_BASE}/category/?q=${encodeURIComponent(searchTerm)}&limit=20`
        : `${CRM_API_BASE}/category/?limit=20`

      const response = await httpJson<{ results?: Category[] }>(url)

      if (response.results) {
        if (searchTerm) {
          setCategories(response.results)
        } else {
          setInitialCategories(response.results)
          setCategories(response.results)
        }
        setCategoriesLoaded(true)
      } else {
        if (!searchTerm) {
          setInitialCategories([])
        }
        setCategories([])
      }
    } catch (error) {
      if (error instanceof Error && error.message === "Request was cancelled") {
        return
      }
      console.error("Error fetching categories:", error)
      setCategories([])
    } finally {
      setLoadingCategories(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showWantsDropdown) {
        if (wantsSearchTerm.trim().length >= 2) {
          fetchCategories(wantsSearchTerm)
        } else if (wantsSearchTerm.trim().length === 0 && !categoriesLoaded) {
          fetchCategories()
        }
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [wantsSearchTerm, showWantsDropdown, categoriesLoaded])


  useEffect(() => {
    const timer = setTimeout(() => {
      if (showAvoidDropdown) {
        if (avoidSearchTerm.trim().length >= 2) {
          fetchCategories(avoidSearchTerm)
        } else if (avoidSearchTerm.trim().length === 0 && !categoriesLoaded) {
          fetchCategories()
        }
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [avoidSearchTerm, showAvoidDropdown, categoriesLoaded])

  const addWantCategory = (category: Category) => {
    // If "All Categories" is selected, deselect it when adding individual category
    if (isAllCategoriesSelected) {
      setIsAllCategoriesSelected(false)
      setAllCategories([])
    }
    
    if (!wantsList.some((w) => w.id === category.id)) {
      setAvoidList(avoidList.filter((a) => a.id !== category.id))
      setWantsList([...wantsList, category])
      setWantsSearchTerm("")
      setShowWantsDropdown(false)
      if (wantsError) {
        setWantsError(false)
      }
    }
  }

  const fetchAllCategories = async (): Promise<Category[]> => {
    const allCats: Category[] = []
    let nextUrl: string | null = `${CRM_API_BASE}/category/?limit=100`
    let pageCount = 0
    const maxPages = 50
    
    try {
      setLoadingAllCategories(true)
      while (nextUrl && pageCount < maxPages) {
        const response = await httpJson<{ 
          results?: Category[], 
          next: string | null,
          count?: number 
        }>(nextUrl)
        
        if (response.results && response.results.length > 0) {
          allCats.push(...response.results)
          console.log(`[v0] Fetched ${response.results.length} categories (total so far: ${allCats.length})`)
        }
        
        nextUrl = response.next || null
        pageCount++
        
        if (!response.next || (response.results && response.results.length === 0)) {
          break
        }
      }
      
      console.log(`[v0] Total categories fetched: ${allCats.length}`)
      return allCats
    } catch (error) {
      console.error("Error fetching all categories:", error)
      return allCats
    } finally {
      setLoadingAllCategories(false)
    }
  }

  const addAllWantCategories = async () => {
    // Show badge immediately for instant feedback
    setIsAllCategoriesSelected(true)
    setWantsList([])
    setWantsSearchTerm("")
    setShowWantsDropdown(false)
    if (wantsError) {
      setWantsError(false)
    }
    
    // Fetch all categories in background
    try {
      const allCats = await fetchAllCategories()
      setAllCategories(allCats)
    } catch (error) {
      console.error("Error fetching all categories:", error)
      // If fetch fails, deselect "All Categories"
      setIsAllCategoriesSelected(false)
    }
  }

  const removeWantCategory = (categoryId: number) => {
    setWantsList(wantsList.filter((w) => w.id !== categoryId))
  }

  const removeAllCategories = () => {
    setIsAllCategoriesSelected(false)
    setAllCategories([])
  }

  const addAvoidCategory = (category: Category) => {
    if (!avoidList.some((a) => a.id === category.id)) {
      // If "All Categories" is selected, we don't need to remove from wantsList
      // because the effective wants will be calculated dynamically (allCategories - avoids)
      // But if individual categories are selected, remove from wantsList
      if (!isAllCategoriesSelected) {
      setWantsList(wantsList.filter((w) => w.id !== category.id))
      }
      setAvoidList([...avoidList, category])
      setAvoidSearchTerm("")
      setShowAvoidDropdown(false)
      if (avoidError) {
        setAvoidError(false)
      }
    }
  }

  const removeAvoidCategory = (categoryId: number) => {
    setAvoidList(avoidList.filter((a) => a.id !== categoryId))
  }

  const addLinkInfoRow = () => {
    const newRow: LinkInfoRow = {
      id: Date.now().toString(),
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipcode: "",
      targetUrl: "",
      anchor: "",
      linkCount: "",
      da: "",
      dr: "",
      traffic: "",
      spam: "",
    }
    setLinkInfoRows([...linkInfoRows, newRow])
  }

  const removeLinkInfoRow = (id: string) => {
    if (linkInfoRows.length > 1) {
      setLinkInfoRows(linkInfoRows.filter((row) => row.id !== id))
    }
  }

  const updateLinkInfoRow = (id: string, field: keyof LinkInfoRow, value: string) => {
    setLinkInfoRows(
      linkInfoRows.map((row) => {
        if (row.id === id) {
          return { ...row, [field]: value }
        }
        return row
      }),
    )
    
    if (field !== "linkCount" && linkInfoRowsErrors[id]?.has(field)) {
      const newErrors = { ...linkInfoRowsErrors }
      newErrors[id].delete(field)
      if (newErrors[id].size === 0) {
        delete newErrors[id]
      }
      setLinkInfoRowsErrors(newErrors)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Reset all error states
    setClientError(false)
    setTotalBudgetError(false)
    setTotalLinksNeededError(false)
    setDueDateError(false)
    setLinkInfoRowsErrors({})
    setOpportunityTypesError(false)
    setWantsError(false)
    setAvoidError(false)
    setAnchorRequirementError(false)
    setRecurringMonthlyError(false)
    setFlexibleOnMetricsError(false)
    setFlexibleOnWantsAvoidsError(false)

    // Collect all validation errors and track which fields have errors
    const errors: string[] = []
    const rowErrors: Record<string, Set<string>> = {}
    
    // Track which fields have errors (using local variables instead of state)
    let hasClientError = false
    let hasTotalBudgetError = false
    let hasTotalLinksNeededError = false
    let hasTotalLinksNeededExceedsError = false
    let hasDueDateError = false
    let hasRecurringMonthlyError = false
    let hasLinkInfoRowsError = false
    let hasAnchorRequirementError = false
    let hasOpportunityTypesError = false
    let hasWantsError = false
    let hasAvoidError = false
    let hasFlexibleOnMetricsError = false
    let hasFlexibleOnWantsAvoidsError = false

    if (!clientId) {
      setClientError(true)
      hasClientError = true
      errors.push("Please select a client")
    }

    if (!totalBudget.trim()) {
      setTotalBudgetError(true)
      hasTotalBudgetError = true
      errors.push("Total Budget is required")
    }

    if (!totalLinksNeeded.trim()) {
      setTotalLinksNeededError(true)
      hasTotalLinksNeededError = true
      errors.push("Total Links Needed is required")
    }

    const totalLinksNeededNum = Number.parseInt(totalLinksNeeded) || 0
    const totalLinksPerUrl = linkInfoRows.reduce((sum, row) => {
      const linkCount = Number.parseInt(row.linkCount) || 0
      return sum + linkCount
    }, 0)

    // Links per URL are optional, but cannot exceed Total Links Needed
    // If sum exceeds Total Links Needed, block submission
    if (totalLinksNeededNum > 0 && totalLinksPerUrl > totalLinksNeededNum) {
      errors.push(`Sum of Links per URL (${totalLinksPerUrl}) exceeds Total Links Needed (${totalLinksNeededNum}). Please reduce Links per URL or increase Total Links Needed.`)
    }

    if (!dueDate) {
      setDueDateError(true)
      hasDueDateError = true
      errors.push("Due Date is required")
    }

    // TODO: Temporarily disabled - Link Information validation
    // Validate link info rows
    // if (linkInfoRows.length === 0) {
    //   hasLinkInfoRowsError = true
    //   errors.push("Please add at least one link information row")
    // } else {
    //   for (let i = 0; i < linkInfoRows.length; i++) {
    //     const row = linkInfoRows[i]
    //     const rowErrorFields = new Set<string>()
    //     
    //     if (!row.targetUrl.trim()) {
    //       rowErrorFields.add("targetUrl")
    //       errors.push(`Row ${i + 1}: Target URL is required`)
    //     }
    //     if (!row.anchor.trim()) {
    //       rowErrorFields.add("anchor")
    //       errors.push(`Row ${i + 1}: Preferred Anchor is required`)
    //     }
    //     if (!row.location.trim()) {
    //       rowErrorFields.add("location")
    //       errors.push(`Row ${i + 1}: Location is required`)
    //     }
    //     if (!row.linkCount.trim()) {
    //       rowErrorFields.add("linkCount")
    //       errors.push(`Row ${i + 1}: Links per URL is required`)
    //     }
    //     if (!row.da.trim()) {
    //       rowErrorFields.add("da")
    //       errors.push(`Row ${i + 1}: DA (minimum) is required`)
    //     }
    //     if (!row.dr.trim()) {
    //       rowErrorFields.add("dr")
    //       errors.push(`Row ${i + 1}: DR (minimum) is required`)
    //     }
    //     if (!row.traffic.trim()) {
    //       rowErrorFields.add("traffic")
    //       errors.push(`Row ${i + 1}: Traffic (minimum) is required`)
    //     }
    //     if (!row.spam.trim()) {
    //       rowErrorFields.add("spam")
    //       errors.push(`Row ${i + 1}: Spam (minimum) is required`)
    //     }
    //     
    //     if (rowErrorFields.size > 0) {
    //       rowErrors[row.id] = rowErrorFields
    //       hasLinkInfoRowsError = true
    //     }
    //   }
    // }

    // if (Object.keys(rowErrors).length > 0) {
    //   setLinkInfoRowsErrors(rowErrors)
    // }

    // Validate at least one opportunity type is selected
    const hasOpportunityType = Object.values(opportunityTypes).some((value) => value)
    if (!hasOpportunityType) {
      setOpportunityTypesError(true)
      hasOpportunityTypesError = true
      errors.push("Please select at least one opportunity type")
    }

    // Validate at least one want category is selected (either individual or all categories)
    if (wantsList.length === 0 && !isAllCategoriesSelected) {
      setWantsError(true)
      hasWantsError = true
      errors.push("Please add at least one category to Wants")
    }

    // Validate at least one avoid category is selected
    if (avoidList.length === 0) {
      setAvoidError(true)
      hasAvoidError = true
      errors.push("Please add at least one category to Avoids")
    }

    // Validate radio button selections
    if (!anchorRequirement) {
      hasAnchorRequirementError = true
      setAnchorRequirementError(true)
      errors.push("Please select Anchor Text Required option")
    }

    if (!recurringMonthly) {
      setRecurringMonthlyError(true)
      hasRecurringMonthlyError = true
      errors.push("Please select Recurring Order option")
    }

    if (recurringMonthly === "yes") {
      if (!recurringFrequency) {
        errors.push("Please select Recurring Frequency")
      }
      const hasEndDate =
        (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) ||
        (recurringFrequency === "Quarterly" && selectedEndQuarter !== null && selectedEndYear !== null)
      if (recurringFrequency && !hasEndDate) {
        setRecurringMonthlyError(true)
        hasRecurringMonthlyError = true
        errors.push(`Please select Recurring End ${recurringFrequency === "Monthly" ? "Month" : "Quarter"}`)
      }
    }

    if (!flexibleOnMetrics) {
      setFlexibleOnMetricsError(true)
      hasFlexibleOnMetricsError = true
      errors.push("Please select Flexible on Metrics option")
    }

    if (!flexibleOnWantsAvoids) {
      setFlexibleOnWantsAvoidsError(true)
      hasFlexibleOnWantsAvoidsError = true
      errors.push("Please select Flexible on Wants/Avoids option")
    }

    // If there are validation errors, stop here (errors are already displayed in the fields)
    if (errors.length > 0) {
      // Focus on the first field with error (in order of appearance)
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (hasClientError && clientInputRef.current) {
            clientInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
            setTimeout(() => {
              clientInputRef.current?.focus()
            }, 300)
          } else if (hasTotalBudgetError && totalBudgetInputRef.current) {
            totalBudgetInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
            setTimeout(() => {
              totalBudgetInputRef.current?.focus()
            }, 300)
          } else if ((hasTotalLinksNeededError || hasTotalLinksNeededExceedsError) && totalLinksNeededInputRef.current) {
            totalLinksNeededInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
            setTimeout(() => {
              totalLinksNeededInputRef.current?.focus()
            }, 300)
          } else if (hasDueDateError && dueDateButtonRef.current) {
            dueDateButtonRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
            setTimeout(() => {
              dueDateButtonRef.current?.focus()
            }, 300)
          } else if (hasRecurringMonthlyError && recurringMonthlyRef.current) {
            recurringMonthlyRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasLinkInfoRowsError && linkInfoSectionRef.current) {
            linkInfoSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasAnchorRequirementError && anchorRequirementRef.current) {
            anchorRequirementRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasOpportunityTypesError && opportunityTypesRef.current) {
            opportunityTypesRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasWantsError && wantsRef.current) {
            wantsRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasAvoidError && avoidRef.current) {
            avoidRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasFlexibleOnMetricsError && flexibleOnMetricsRef.current) {
            flexibleOnMetricsRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasFlexibleOnWantsAvoidsError && flexibleOnWantsAvoidsRef.current) {
            flexibleOnWantsAvoidsRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }, 50)
      })
      return
    }

    setIsSubmitting(true)

    try {
      // Step 1: Collect category IDs for wants and avoid
      // If "All Categories" is selected, get all categories minus avoids
      // Otherwise, use individually selected categories
      let effectiveWants: Category[] = []
      if (isAllCategoriesSelected) {
        const avoidIds = avoidList.map(a => a.id)
        effectiveWants = allCategories.filter(cat => !avoidIds.includes(cat.id))
        console.log("[v0] All Categories selected - total categories:", allCategories.length, "minus avoids:", avoidIds.length, "effective wants:", effectiveWants.length)
      } else {
        effectiveWants = wantsList
      }
      
      const wantCategoryIds = effectiveWants.map((c) => c.id)
      console.log("[v0] Sending selected want category IDs:", wantCategoryIds)
      
      const avoidCategoryIds = avoidList.map((c) => c.id)
      console.log("[v0] Avoid category IDs to send:", avoidCategoryIds)

      // Step 2.5: Create combos (Address, URL, AnchorText, Combo) BEFORE the order
      const comboIds: number[] = []
      for (const row of linkInfoRows) {
        const hasLinksNeeded = row.linkCount.trim() &&
          !isNaN(Number.parseInt(row.linkCount.trim())) &&
          Number.parseInt(row.linkCount.trim()) > 0

        if (!row.targetUrl.trim() && !row.anchor.trim() && !hasLinksNeeded) {
          continue
        }

        try {
          let addressId: number | null = null
          if (row.addressLine1.trim() || row.city.trim() || row.state.trim() || row.zipcode.trim()) {
            const addressBody: Record<string, string> = {}
            if (row.addressLine1.trim()) addressBody.line_1 = row.addressLine1.trim()
            if (row.addressLine2.trim()) addressBody.line_2 = row.addressLine2.trim()
            if (row.city.trim()) addressBody.city = row.city.trim()
            if (row.state.trim()) addressBody.state = row.state.trim()
            if (row.zipcode.trim()) addressBody.zip_code = row.zipcode.trim()
            const addressResponse = await httpJson<{ id: number } | { data: { id: number } }>(`${CRM_API_BASE}/address/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(addressBody),
            })
            addressId = (addressResponse as any)?.data?.id || (addressResponse as any)?.id
          }

          let targetUrlId: number | null = null
          if (row.targetUrl.trim()) {
            const urlBody: any = { text: row.targetUrl.trim() }
            if (addressId) urlBody.address = addressId
            const urlResponse = await httpJson<{ id: number } | { data: { id: number } }>(`${CRM_API_BASE}/url/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(urlBody),
            })
            targetUrlId = (urlResponse as any)?.data?.id || (urlResponse as any)?.id
          }

          const needsAnchorText = row.anchor.trim() || hasLinksNeeded
          let anchorTextId: number | null = null
          if (needsAnchorText) {
            const anchorBody: any = { value: row.anchor.trim() || null }
            if (targetUrlId) anchorBody.target_url = targetUrlId
            if (addressId) anchorBody.address = addressId
            const anchorResponse = await httpJson<{ id: number } | { data: { id: number } }>(`${CRM_API_BASE}/anchor_text/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(anchorBody),
            })
            anchorTextId = (anchorResponse as any)?.data?.id || (anchorResponse as any)?.id
          }

          if (anchorTextId) {
            const comboBody: any = { anchor_text: anchorTextId }
            if (hasLinksNeeded) comboBody.links_needed = Number.parseInt(row.linkCount.trim())
            const comboResponse = await httpJson<{ id: number } | { data: { id: number } }>(`${CRM_API_BASE}/combo/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(comboBody),
            })
            const comboId = (comboResponse as any)?.data?.id || (comboResponse as any)?.id
            if (comboId) comboIds.push(comboId)
          }
        } catch (error) {
          console.error("[v0] Error creating combo for row:", row.id, error)
          toast({
            title: "Warning",
            description: "Failed to save some link information. Order will be created without these combos.",
            variant: "destructive",
          })
        }
      }
      console.log("[v0] Combo IDs for order:", comboIds)

      // Step 3: Create the order with all the collected IDs
      const currentDate = new Date().toISOString().slice(0, 19).replace('T', ' ')
      console.log("[v0] Current date for order:", currentDate)

      // Map opportunity types from frontend format to backend format
      const opportunityTypesBackend = {
        general_sponsorship: opportunityTypes.generalDonation,
        event: opportunityTypes.event,
        organization_membership: opportunityTypes.organizationMemberships,
        chamber_of_commerce_membership: opportunityTypes.chamberOfCommerceMemberships,
      }

      const orderData: any = {
        client: Number.parseInt(clientId),
        business: businessId ? Number.parseInt(businessId) : null,
        due_date: dueDate ? dueDate.toISOString().split("T")[0] : null,
        link_count: Number.parseInt(totalLinksNeeded) || 0,
        budget: Number.parseFloat(totalBudget) || 0,
        zs_opp_fee: Number.parseFloat(zsOppFee) || 150,
        stage: 7, // "created" stage
        is_paid: false, // Required field
        locations: [], // Required field
        metrics_requirements: [], // Created after order via POST client_order/<id>/metric_requirement
        connections: [], // Required field
        target_urls: [], // Empty for now - will use new BE model later
        combos: comboIds,
        avoid_categories: avoidCategoryIds, // Send selected avoid category IDs
        businesses: businessId ? [Number.parseInt(businessId)] : [], // Add business ID to businesses array
        date_order: currentDate, // Format: 2025-06-30 18:40:36
        // Recurring order fields (flag on order; future orders created via POST /client_order/{id}/serie)
        recurring_monthly: recurringMonthly === "yes",
        ...(recurringMonthly === "yes" && recurringFrequency && {
          recurring_type: recurringFrequency === "Monthly" ? 1 : 2,
        }),
        anchor_required: anchorRequirement === "required", // Convert "preferred"/"required" to boolean
        flexible_on_metrics: flexibleOnMetrics === "yes", // Convert "yes"/"no" to boolean
        flexible_on_wants_avoids: flexibleOnWantsAvoids === "yes", // Convert "yes"/"no" to boolean
        opportunity_types: opportunityTypesBackend, // Map to backend format
        // TODO: Leave disconnected for now (new BE model):
        // - linkInfoRows (target url, anchor, location, zipcode, links per url) - "Link Information"
      }

      // Add want categories directly (no flag support)
      orderData.want_categories = wantCategoryIds

      console.log("[v0] Creating order with data:", orderData)

      // Use fetch directly to access response headers (Location header might contain the ID)
      const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
      
      const fetchResponse = await fetch(`${CRM_API_BASE}/client_order/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "User-Agent": "CRM-Dashboard/1.0",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(orderData),
      })

      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || `HTTP ${fetchResponse.status}: Failed to create order`)
      }

      const response = await fetchResponse.json().catch(() => ({}))
      console.log("[v0] Order created successfully:", response)
      console.log("[v0] Response structure:", JSON.stringify(response, null, 2))
      
      // Extract order ID from response.data.id (backend wraps response in data object)
      const orderId = response?.data?.id
      
      if (!orderId) {
        console.error("[v0] Order ID not found in response.data.id. Full response:", response)
        throw new Error("Order created but ID not found in response")
      }
      
      console.log("[v0] Order ID extracted:", orderId)

      // Create metric requirements via POST client_order/<id>/metric_requirement (includes is_flexible)
      const metricsToCreate: { name: string; value: number; isFlexible: boolean }[] = []
      if (globalMetrics.da?.trim()) metricsToCreate.push({ name: "da", value: parseFloat(globalMetrics.da), isFlexible: flexiblePerMetric.da })
      if (globalMetrics.dr?.trim()) metricsToCreate.push({ name: "dr", value: parseFloat(globalMetrics.dr), isFlexible: flexiblePerMetric.dr })
      if (globalMetrics.traffic?.trim()) metricsToCreate.push({ name: "traffic", value: parseFloat(globalMetrics.traffic), isFlexible: flexiblePerMetric.traffic })
      if (globalMetrics.spam?.trim()) metricsToCreate.push({ name: "spam", value: parseFloat(globalMetrics.spam), isFlexible: flexiblePerMetric.spam })

      for (const metric of metricsToCreate) {
        try {
          const createBody = {
            name: metric.name,
            operator: "greater_than",
            value: metric.value,
            is_flexible: metric.isFlexible ? 1 : 0,
          }
          await httpJson(
            `${CRM_API_BASE}/client_order/${orderId}/metric_requirement`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(createBody),
            }
          )
        } catch (metricError) {
          console.error("[v0] Error creating metric requirement:", metric.name, metricError)
          toast({
            title: "Warning",
            description: `Failed to save ${metric.name} metric. Order created successfully.`,
            variant: "destructive",
          })
        }
      }

      // Create note with type 10 (order_instructions) if other_requirements is provided
      if (otherRequirements && otherRequirements.trim()) {
        try {
          const noteData = {
            content: otherRequirements.trim(),
            type: 10 // order_instructions
          }
          
          console.log("[v0] Creating order instruction note with data:", noteData)
          console.log("[v0] Order ID for note creation:", orderId)
          
          await httpJson(`${CRM_API_BASE}/client_order/${orderId}/note`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "User-Agent": "CRM-Dashboard/1.0",
            },
            body: JSON.stringify(noteData),
          })
          
          console.log("[v0] Order instruction note created successfully")
        } catch (noteError) {
          console.error("[v0] Error creating order instruction note:", noteError)
          // Don't fail the whole order creation if note creation fails
          toast({
            title: "Warning",
            description: "Order created but failed to save order instructions note",
            variant: "destructive",
          })
        }
      }

      // Create recurring serie when user chose monthly or quarterly (generates future orders)
      // starts_at = start of second order (first clone), so backend does not duplicate the original
      // ends_at = end of last selected month/quarter so that period is included
      const hasRecurringEndDate =
        (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) ||
        (recurringFrequency === "Quarterly" && selectedEndQuarter !== null && selectedEndYear !== null)

      if (
        recurringMonthly === "yes" &&
        (recurringFrequency === "Monthly" || recurringFrequency === "Quarterly") &&
        hasRecurringEndDate
      ) {
        const now = new Date()
        let firstRecurringStart: Date
        if (recurringFrequency === "Monthly") {
          const day = now.getUTCDate()
          const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
          const lastDayOfNextMonth = new Date(Date.UTC(nextMonth.getUTCFullYear(), nextMonth.getUTCMonth() + 1, 0)).getUTCDate()
          const dayInMonth = Math.min(day, lastDayOfNextMonth)
          firstRecurringStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, dayInMonth))
        } else {
          const y = now.getUTCFullYear()
          const m = now.getUTCMonth()
          const quarterIndex = Math.floor(m / 3) + 1
          const nextMonth = quarterIndex * 3
          const nextYear = nextMonth >= 12 ? y + 1 : y
          const nextMonthVal = nextMonth >= 12 ? 0 : nextMonth
          firstRecurringStart = new Date(Date.UTC(nextYear, nextMonthVal, 1))
        }
        const startsAt = firstRecurringStart.toISOString()

        let endDateForSerie: Date
        if (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) {
          const day = now.getUTCDate()
          const nextMonth = selectedEndMonth + 1
          const nextYear = nextMonth >= 12 ? selectedEndYear + 1 : selectedEndYear
          const nextMonthVal = nextMonth >= 12 ? 0 : nextMonth
          const lastDayOfMonth = new Date(Date.UTC(nextYear, nextMonthVal + 1, 0)).getUTCDate()
          const dayInMonth = Math.min(day, lastDayOfMonth)
          endDateForSerie = new Date(Date.UTC(nextYear, nextMonthVal, dayInMonth))
        } else {
          const q = selectedEndQuarter!
          const y = selectedEndYear!
          const nextQ = q + 1
          const nextYear = nextQ >= 4 ? y + 1 : y
          const nextQVal = nextQ >= 4 ? 0 : nextQ
          const nextMonth = nextQVal * 3
          endDateForSerie = new Date(Date.UTC(nextYear, nextMonth, 1))
        }
        const endsAt = endDateForSerie.toISOString()

        const frequency = recurringFrequency === "Monthly" ? 1 : 2
        try {
          await httpJson(`${CRM_API_BASE}/client_order/${orderId}/serie`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              starts_at: startsAt,
              ends_at: endsAt,
              frequency,
            }),
          })
        } catch (serieError: unknown) {
          const message = serieError instanceof Error ? serieError.message : "Failed to create recurring serie"
          const is400 =
            typeof message === "string" &&
            (message.includes("client_order_already_belongs_to_a_serie") || message.includes("bad_request") || message.includes("400"))
          toast({
            title: is400 ? "Recurring serie not created" : "Error",
            description: is400
              ? "Order was created but the recurring serie could not be created (invalid dates or order already in a serie)."
              : message,
            variant: "destructive",
          })
        }
      }

      toast({
        title: "Success",
        description: "Order created successfully",
      })

      if (asModal && onSave) {
        await onSave(response)
        if (onClose) {
          onClose()
        }
      } else if (router) {
        // Navigate to the created order
        router.push(`/orders/${orderId}`)
      }
    } catch (error) {
      // Handle cancelled requests (happens during logout)
      if (error instanceof Error && error.message === "Request was cancelled") {
        console.log("[v0] Order creation was cancelled (likely due to logout)")
        return
      }
      
      console.error("Error creating order:", error)
      toast({
        title: "Error",
        description: "Failed to create order. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const formContent = (
    <>

      <form onSubmit={handleSubmit} className="space-y-8" noValidate>
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Dropdown */}
            <div>
              <Label>
                Client <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <div className="relative">
                  <Input
                    placeholder={
                      loadingClients
                        ? "Loading clients..."
                        : selectedClient && !clientSearchTerm
                          ? ""
                          : "Search clients..."
                    }
                    value={
                      clientId && !clientSearchTerm
                        ? selectedClient?.company_name ||
                          `${selectedClient?.first_name || ""} ${selectedClient?.last_name || ""}`.trim() ||
                          ""
                        : clientSearchTerm
                    }
                    onMouseDown={(e) => {
                      // Open dropdown when user clicks on the input
                      e.preventDefault()
                      setShowClientDropdown(true)
                      setClientSearchTerm("")
                      if (!clientsLoaded) {
                        fetchClients()
                      }
                      // Focus the input after opening dropdown
                      setTimeout(() => {
                        ;(e.target as HTMLInputElement).focus()
                      }, 0)
                    }}
                    onFocus={() => {
                      // Only fetch clients if dropdown is not already shown (to avoid auto-opening on modal open)
                      if (!showClientDropdown && !clientsLoaded) {
                        fetchClients()
                      }
                    }}
                    onChange={(e) => {
                      const searchValue = e.target.value
                      setClientSearchTerm(searchValue)
                      setShowClientDropdown(true)

                      if (selectedClient) {
                        setClientId("")
                      }

                      if (!searchValue.trim() || searchValue.trim().length < 2) {
                        if (clientAbortController) {
                          clientAbortController.abort()
                          setClientAbortController(null)
                        }

                        let clientsToShow = [...initialClients]
                        if (clientId) {
                          const selectedClient = clients.find((client) => client.id.toString() === clientId)
                          if (selectedClient && !clientsToShow.some((client) => client.id === selectedClient.id)) {
                            clientsToShow = [selectedClient, ...clientsToShow]
                          }
                        }

                        setClients(clientsToShow)
                        setLoadingClients(false)
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowClientDropdown(false), 200)
                    }}
                    className={`border-gray-400 focus:border-gray-400 focus:ring-0 pr-20 h-9 w-full ${clientError ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                    disabled={loadingClients}
                  />
                  {(clientSearchTerm || selectedClient) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()

                        const currentClientId = clientId

                        setClientSearchTerm("")
                        setClientId("")
                        setClientError(false)

                        let clientsToShow = [...initialClients]
                        if (currentClientId) {
                          const selectedClient = clients.find((client) => client.id.toString() === currentClientId)
                          if (selectedClient && !clientsToShow.some((client) => client.id === selectedClient.id)) {
                            clientsToShow = [selectedClient, ...clientsToShow]
                          }
                        }
                        setClients(clientsToShow)

                        setShowClientDropdown(true)
                      }}
                      className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded p-1 z-20"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!showClientDropdown) {
                      setShowClientDropdown(true)
                      setClientSearchTerm("")
                      if (!clientsLoaded) {
                        fetchClients()
                      }
                    } else {
                      setShowClientDropdown(false)
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                  disabled={loadingClients}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${showClientDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showClientDropdown && (
                  <div
                    className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal"
                    style={{ left: "0", width: "100%" }}
                  >
                    {clientSearchTerm.trim().length > 0 && clientSearchTerm.trim().length < 2 ? (
                      <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                    ) : loadingClients ? (
                      <div className="p-3 text-sm text-gray-600">Searching clients...</div>
                    ) : clients.length === 0 ? (
                      <div className="p-3 text-sm text-gray-600">
                        {clientSearchTerm.trim()
                          ? `No clients found for "${clientSearchTerm}"`
                          : "No clients available"}
                      </div>
                    ) : (
                      <div>
                        {(clientSearchTerm.trim() === ""
                          ? initialClients
                          : clients.filter((client) => {
                              const clientName =
                                client.company_name || `${client.first_name || ""} ${client.last_name || ""}`.trim()
                              return clientName.toLowerCase().includes(clientSearchTerm.toLowerCase())
                            })
                        ).map((client) => (
                          <div
                            key={client.id}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => {
                              setClientId(client.id.toString())
                              setBusinessId("")
                              setBusinessSearchTerm("")
                              setClientSearchTerm("")
                              if (clientError) {
                                setClientError(false)
                              }
                              setShowClientDropdown(false)
                              setClientError(false)
                            }}
                          >
                            {client.company_name ||
                              `${client.first_name || ""} ${client.last_name || ""}`.trim() ||
                              "Unknown Client"}
                          </div>
                        ))}
                        {(clientSearchTerm.trim() === "" ? initialClients.length === 20 : clients.length === 20) && (
                          <div className="p-2 text-xs text-muted-foreground border-t bg-gray-50">
                            💡 Type a client name to search for more results
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {clientError && <p className="text-sm text-red-600 mt-1">Please select a client</p>}
              {selectedClient && selectedClient.email && !clientError && (
                <p className="text-sm text-muted-foreground mt-1">{selectedClient.email}</p>
              )}
            </div>

            {/* Business Dropdown */}
            <div>
              <Label>
                Business
              </Label>
              <div className="relative">
                <Input
                  placeholder={
                    !clientId
                      ? "Select a client first"
                      : businesses.length === 0
                        ? "No businesses available for this client"
                        : "Search businesses..."
                  }
                  value={selectedBusiness && !businessSearchTerm ? selectedBusiness.name : businessSearchTerm}
                  onMouseDown={(e) => {
                    if (clientId && businesses.length > 0 && !showBusinessDropdown) {
                      e.preventDefault()
                      setShowBusinessDropdown(true)
                      setTimeout(() => (e.target as HTMLInputElement).focus(), 0)
                    }
                  }}
                  onChange={(e) => {
                    setBusinessSearchTerm(e.target.value)
                    if (businesses.length > 0) {
                      setShowBusinessDropdown(true)
                    }
                    if (selectedBusiness) {
                      setBusinessId("")
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowBusinessDropdown(false), 200)
                  }}
                  className="border-gray-400 focus:border-gray-400 focus:ring-0 pr-20 h-9 w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50"
                  disabled={!clientId || businesses.length === 0}
                />
                {(businessSearchTerm || selectedBusiness) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setBusinessSearchTerm("")
                      setBusinessId("")
                      if (businesses.length > 0) {
                        setShowBusinessDropdown(true)
                      }
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded p-1 z-20"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (clientId && businesses.length > 0 && !showBusinessDropdown) {
                      setShowBusinessDropdown(true)
                      setBusinessSearchTerm("")
                    } else if (showBusinessDropdown) {
                      setShowBusinessDropdown(false)
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                  disabled={!clientId || businesses.length === 0}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${showBusinessDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showBusinessDropdown && clientId && businesses.length > 0 && (
                  <div
                    className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal"
                    style={{ left: "0", width: "100%" }}
                  >
                  {businessSearchTerm.trim().length > 0 && businessSearchTerm.trim().length < 2 ? (
                    <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                  ) : businesses.length === 0 ? (
                    <div className="p-3 text-sm text-gray-600">
                      {businessSearchTerm.trim()
                        ? `No businesses found for "${businessSearchTerm}"`
                        : "No businesses available"}
                    </div>
                  ) : (
                    <div>
                      {filteredBusinesses
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((business) => (
                          <div
                            key={business.id}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => {
                              setBusinessId(business.id.toString())
                              setBusinessSearchTerm("")
                              setShowBusinessDropdown(false)
                            }}
                          >
                            {business.name}
                            {business.website && <div className="text-xs text-gray-500 mt-1">{business.website}</div>}
                          </div>
                        ))}
                    </div>
                  )}
                  </div>
                )}
              </div>

              {selectedBusiness && selectedBusiness.website && (
                <p className="text-sm text-muted-foreground mt-1">{selectedBusiness.website}</p>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Budget</h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div>
              <Label htmlFor="totalBudget">
                Total Budget <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  ref={totalBudgetInputRef}
                  id="totalBudget"
                  type="number"
                  placeholder="5000"
                  value={totalBudget}
                  onChange={(e) => {
                    setTotalBudget(e.target.value)
                    if (totalBudgetError) {
                      setTotalBudgetError(false)
                    }
                  }}
                  className={`pl-8 ${totalBudgetError ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                />
              </div>
              {totalBudgetError && <p className="text-sm text-red-600 mt-1">Total Budget is required</p>}
            </div>

            <div>
              <Label htmlFor="totalLinksNeeded">
                Total Links Needed <span className="text-red-500">*</span>
              </Label>
              <Input
                ref={totalLinksNeededInputRef}
                id="totalLinksNeeded"
                type="number"
                placeholder="10"
                value={totalLinksNeeded}
                onChange={(e) => {
                  const newValue = e.target.value
                  setTotalLinksNeeded(newValue)
                  
                  if (totalLinksNeededError && newValue.trim()) {
                    setTotalLinksNeededError(false)
                  }
                  
                }}
                className={`${totalLinksNeededError ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
              />
              {totalLinksNeededError && (
                <p className="text-sm text-red-600 mt-1">Total Links Needed is required</p>
              )}
            </div>

            <div>
              <Label htmlFor="zsOppFee">
                ZipSprout Fee (per link)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="zsOppFee"
                  type="number"
                  placeholder="150"
                  value={zsOppFee}
                  onChange={(e) => setZsOppFee(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            <div>
              <Label>Cost per Sponsorship</Label>
              <div className="h-10 px-3 py-2 border rounded-md bg-muted flex items-center">
                <span className="text-lg font-bold text-blue-600">${costPerSponsorship}</span>
              </div>
              <p className="text-xs text-muted-foreground/60 mt-1">Total Budget ÷ Total Links Needed</p>
            </div>

            <div>
              <Label>Net Budget per Sponsorship</Label>
              <div className="h-10 px-3 py-2 border rounded-md bg-muted flex items-center">
                <span className="text-lg font-bold text-green-600">${pricePerSponsorship}</span>
              </div>
              <p className="text-xs text-muted-foreground/60 mt-1">(Total Budget ÷ Total Links Needed) - ZipSprout Fee</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>
                Due Date <span className="text-red-500">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    ref={dueDateButtonRef}
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-10 border border-gray-400 bg-white hover:bg-gray-50 hover:text-foreground",
                      !dueDate && "text-muted-foreground",
                      dueDateError && "border-red-500 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-600"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate
                      ? dueDate.toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar 
                    mode="single" 
                    selected={dueDate} 
                    onSelect={(date) => {
                      setDueDate(date)
                      if (dueDateError) {
                        setDueDateError(false)
                      }
                    }} 
                    initialFocus 
                  />
                </PopoverContent>
              </Popover>
              {dueDateError && <p className="text-sm text-red-600 mt-1">Due Date is required</p>}
            </div>

            <div ref={recurringMonthlyRef} className="space-y-2">
              <Label>
                Recurring Order <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-6 pt-2">
                <RadioGroup
                  value={recurringMonthly || ""}
                  onValueChange={(value) => {
                    setRecurringMonthly(value as "yes" | "no")
                    if (recurringMonthlyError) {
                      setRecurringMonthlyError(false)
                    }
                    if (value === "no") {
                      setRecurringFrequency(undefined)
                      setEndDate(undefined)
                      setEndDateError(false)
                      setSelectedEndMonth(null)
                      setSelectedEndYear(null)
                      setSelectedEndQuarter(null)
                    }
                  }}
                  className={cn(
                    "flex gap-6",
                    recurringMonthlyError && "border border-red-500 rounded-md px-3 py-2 bg-red-50"
                  )}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="recurring-yes" />
                    <Label htmlFor="recurring-yes" className="font-normal cursor-pointer">
                      Yes
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="recurring-no" />
                    <Label htmlFor="recurring-no" className="font-normal cursor-pointer">
                      No
                    </Label>
                  </div>
                </RadioGroup>

                {recurringMonthly === "yes" && (
                  <div className="flex items-center gap-4 ml-4">
                    <Select value={recurringFrequency || ""} onValueChange={handleFrequencyChange}>
                      <SelectTrigger className="border border-gray-400 bg-white w-[180px] h-10">
                        <SelectValue placeholder="Select frequency..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="Quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {recurringMonthlyError && <p className="text-sm text-red-600 mt-1">Please select Recurring Order option</p>}

              {recurringMonthly === "yes" && recurringFrequency && (
                <div className="space-y-2 mt-4">
                  <Label>
                    Recurring End {recurringFrequency === "Monthly" ? "Month" : "Quarter"}
                  </Label>
                  <div className="relative" ref={pickerRef}>
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date()
                        if (!showPicker && pickerYear < today.getFullYear()) {
                          setPickerYear(today.getFullYear())
                        }
                        setShowPicker(!showPicker)
                      }}
                      className="flex items-center w-full h-10 px-3 pl-10 rounded-md border border-gray-400 bg-white text-sm text-left hover:bg-gray-50 transition-colors"
                    >
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      {getEndDateDisplay() || (
                        <span className="text-muted-foreground">
                          Select {recurringFrequency === "Monthly" ? "month" : "quarter"}...
                        </span>
                      )}
                    </button>

                    {showPicker && (
                      <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-border rounded-lg shadow-lg p-4 w-[300px]">
                        <div className="flex items-center justify-between mb-4">
                          <button
                            type="button"
                            onClick={() => setPickerYear(pickerYear - 1)}
                            disabled={pickerYear <= new Date().getFullYear()}
                            className="p-1 rounded hover:bg-foreground/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="font-medium text-sm">{pickerYear}</span>
                          <button
                            type="button"
                            onClick={() => setPickerYear(pickerYear + 1)}
                            className="p-1 rounded hover:bg-foreground/5 transition-colors"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>

                        {recurringFrequency === "Monthly" ? (
                          <div className="grid grid-cols-3 gap-2">
                            {MONTHS.map((month, index) => {
                              const isSelected = selectedEndMonth === index && selectedEndYear === pickerYear
                              const today = new Date()
                              const monthDate = new Date(pickerYear, index, 1)
                              const isPast = monthDate < new Date(today.getFullYear(), today.getMonth(), 1)
                              return (
                                <button
                                  key={month}
                                  type="button"
                                  onClick={() => !isPast && handleSelectMonth(index)}
                                  disabled={isPast}
                                  className={cn(
                                    "py-2 px-3 rounded-md text-sm font-normal transition-colors",
                                    isSelected
                                      ? "bg-primary text-white"
                                      : "hover:bg-foreground/5 text-foreground",
                                    isPast && "opacity-50 cursor-not-allowed hover:bg-transparent"
                                  )}
                                >
                                  {month}
                                </button>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {QUARTERS.map((quarter, index) => {
                              const isSelected = selectedEndQuarter === index && selectedEndYear === pickerYear
                              const endMonthOfQuarter = QUARTERS[index].startMonth + 2
                              const quarterEndDate = new Date(pickerYear, endMonthOfQuarter + 1, 0)
                              const isPast = quarterEndDate < new Date()
                              return (
                                <button
                                  key={quarter.label}
                                  type="button"
                                  onClick={() => !isPast && handleSelectQuarter(index)}
                                  disabled={isPast}
                                  className={cn(
                                    "py-3 px-3 rounded-md text-sm transition-colors text-center",
                                    isSelected
                                      ? "bg-primary text-white"
                                      : "hover:bg-foreground/5 text-foreground",
                                    isPast && "opacity-50 cursor-not-allowed hover:bg-transparent"
                                  )}
                                >
                                  <div className="font-medium">{quarter.label}</div>
                                  <div className={cn("text-xs mt-0.5", isSelected ? "text-white/70" : "text-muted-foreground")}>
                                    {quarter.months}
                                  </div>
                                </button>
                              )
                            })}
                          </div>
                        )}

                        {(selectedEndMonth !== null || selectedEndQuarter !== null) && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedEndMonth(null)
                              setSelectedEndYear(null)
                              setSelectedEndQuarter(null)
                              setEndDate(undefined)
                              setShowPicker(false)
                            }}
                            className="mt-3 w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                          >
                            Clear selection
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {recurringMonthly === "yes" && recurringFrequency && recurringSeries.length === 0 && (
                <p className="text-sm text-muted-foreground mt-4">
                  Select Recurring End {recurringFrequency === "Monthly" ? "Month" : "Quarter"} to see the Order Series Breakdown
                </p>
              )}
              {recurringSeries.length > 0 && (
                <div className="mt-4">
                  <Label className="text-sm font-normal mb-2 block">
                    Order Series Breakdown
                  </Label>
                  <div className="border border-border rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted border-b border-border">
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-20">
                            #
                          </th>
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                            Month
                          </th>
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                            Start Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {recurringSeries.map((entry) => (
                          <tr
                            key={entry.number}
                            className="border-b border-border last:border-b-0 hover:bg-muted/50"
                          >
                            <td className="px-4 py-2.5 text-muted-foreground font-medium">
                              {entry.number}
                            </td>
                            <td className="px-4 py-2.5 text-foreground">
                              {entry.month}
                            </td>
                            <td className="px-4 py-2.5 text-foreground">
                              {entry.startDate}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {recurringSeries.length} order{recurringSeries.length !== 1 ? "s" : ""} will be created based on the current configuration
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-6" ref={linkInfoSectionRef}>
          <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold">Link Information</h2>
                <p className="text-sm text-muted-foreground mt-1">Add target URLs, anchor text, and address details</p>
              </div>
            <Button type="button" onClick={addLinkInfoRow} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          </div>

          {(totalLinksNeededNum > 0 && (isOverLimit || isUnderLimit)) && (
            <div className={`mb-4 p-3 rounded-md border ${
              isOverLimit
                ? "bg-red-50 border-red-300" 
                : isUnderLimit
                ? "bg-yellow-50 border-yellow-300" 
                : "bg-gray-50 border-gray-300"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className={`h-4 w-4 ${
                    isOverLimit
                      ? "text-red-600" 
                      : isUnderLimit
                      ? "text-yellow-600" 
                      : "text-gray-600"
                  }`} />
                  <span className={`text-sm font-medium ${
                    isOverLimit
                      ? "text-red-700" 
                      : isUnderLimit
                      ? "text-yellow-700" 
                      : "text-gray-700"
                  }`}>
                    Links per URL: {totalLinksPerUrl} {totalLinksNeededNum > 0 ? `/ ${totalLinksNeededNum}` : ""}
                  </span>
                </div>
                {totalLinksNeededNum > 0 && (
                  <span className={`text-sm font-semibold ${
                    isOverLimit
                      ? "text-red-700" 
                      : isUnderLimit
                      ? "text-yellow-700"
                      : "text-green-700"
                  }`}>
                    {isOverLimit 
                      ? `Exceeds by ${Math.abs(remainingLinks)}` 
                      : isUnderLimit
                        ? `Missing ${Math.abs(remainingLinks)}`
                        : `${remainingLinks} remaining`}
                  </span>
                )}
                {totalLinksNeededNum === 0 && totalLinksPerUrl > 0 && (
                  <span className="text-sm text-gray-600">
                    Set "Total Links Needed" to validate
                  </span>
                )}
              </div>
              {isOverLimit && (
                <p className="text-xs text-red-600 mt-1">
                  Sum of Links per URL ({totalLinksPerUrl}) exceeds Total Links Needed ({totalLinksNeededNum}). Please reduce Links per URL or increase Total Links Needed.
                </p>
              )}
              {isUnderLimit && (
                <p className="text-xs text-yellow-600 mt-1">
                  Sum of Links per URL ({totalLinksPerUrl}) is less than Total Links Needed ({totalLinksNeededNum}). You can still create the order.
                </p>
              )}
            </div>
          )}

          <div className="space-y-4">
            {linkInfoRows.map((row, index) => {
              const currentLinkCount = Number.parseInt(row.linkCount) || 0
              const otherRowsTotal = totalLinksPerUrl - currentLinkCount
              const maxForThisRow = totalLinksNeededNum > 0 ? totalLinksNeededNum - otherRowsTotal : Infinity
              const isRowOverLimit = totalLinksNeededNum > 0 && currentLinkCount > maxForThisRow
              
              return (
              <Card key={row.id}>
                {index === 0 && (
                  <div className="grid grid-cols-12 gap-4 p-4 bg-muted/50 border-b font-medium text-sm">
                    <div className="col-span-3 flex items-center gap-2">
                      <LinkIcon className="h-4 w-4 text-muted-foreground" />
                      <span>Target URL</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      <span>Preferred Anchor</span>
                    </div>
                    <div className="col-span-5 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>Address</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2 justify-end">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span>Links per URL</span>
                    </div>
                  </div>
                )}

                <div className="p-4">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-3">
                      <Input
                        type="url"
                        value={row.targetUrl}
                        onChange={(e) => updateLinkInfoRow(row.id, "targetUrl", e.target.value)}
                        placeholder="https://example.com"
                        className={`h-9 ${linkInfoRowsErrors[row.id]?.has("targetUrl") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                      />
                    </div>

                    <div className="col-span-2">
                      <Input
                        value={row.anchor}
                        onChange={(e) => updateLinkInfoRow(row.id, "anchor", e.target.value)}
                        placeholder="Preferred anchor text"
                        className={`h-9 ${linkInfoRowsErrors[row.id]?.has("anchor") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                      />
                    </div>

                    <div className="col-span-5 space-y-2">
                      <Input
                        value={row.addressLine1}
                        onChange={(e) => updateLinkInfoRow(row.id, "addressLine1", e.target.value)}
                        placeholder="Address Line 1"
                        className={`h-9 ${linkInfoRowsErrors[row.id]?.has("addressLine1") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                      />
                      <Input
                        value={row.addressLine2}
                        onChange={(e) => updateLinkInfoRow(row.id, "addressLine2", e.target.value)}
                        placeholder="Address Line 2 (optional)"
                        className={`h-9 ${linkInfoRowsErrors[row.id]?.has("addressLine2") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          value={row.city}
                          onChange={(e) => updateLinkInfoRow(row.id, "city", e.target.value)}
                          placeholder="City"
                          className={`h-9 ${linkInfoRowsErrors[row.id]?.has("city") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                        />
                        <Input
                          value={row.state}
                          onChange={(e) => updateLinkInfoRow(row.id, "state", e.target.value)}
                          placeholder="State"
                          className={`h-9 ${linkInfoRowsErrors[row.id]?.has("state") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                        />
                        <Input
                          value={row.zipcode}
                          onChange={(e) => updateLinkInfoRow(row.id, "zipcode", e.target.value)}
                          placeholder="Zipcode"
                          maxLength={10}
                          className={`h-9 ${linkInfoRowsErrors[row.id]?.has("zipcode") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                        />
                      </div>
                    </div>

                     <div className="col-span-2 flex items-start gap-2 justify-end">
                       <div className="flex-1">
                         <Input
                           type="number"
                           value={row.linkCount}
                           onChange={(e) => {
                             const value = e.target.value
                            updateLinkInfoRow(row.id, "linkCount", value)
                             
                           }}
                          placeholder="5"
                          min="0"
                          className={`h-9 flex-1 ${
                             linkInfoRowsErrors[row.id]?.has("linkCount")
                               ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" 
                               : ""
                           }`}
                         />
                         {linkInfoRowsErrors[row.id]?.has("linkCount") && (
                           <p className="text-xs text-red-600 mt-1">
                             Invalid value
                           </p>
                         )}
                       </div>
                       {linkInfoRows.length > 1 && (
                         <Button
                           type="button"
                           variant="ghost"
                           size="icon"
                           onClick={() => removeLinkInfoRow(row.id)}
                           className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       )}
                     </div>
                   </div>
                 </div>
               </Card>
              )
            })}
          </div>

          <div className="mt-6 pt-6 border-t" ref={anchorRequirementRef}>
            <Label className="text-base font-medium mb-3 block">
              Anchor Text Required? <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={anchorRequirement || ""}
              onValueChange={(value) => {
                setAnchorRequirement(value as "preferred" | "required")
                if (anchorRequirementError) {
                  setAnchorRequirementError(false)
                }
              }}
              className={anchorRequirementError ? "border-red-500" : ""}
            >
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="preferred" id="preferred" />
                  <Label htmlFor="preferred" className="font-normal cursor-pointer">
                    Preferred
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="required" id="required" />
                  <Label htmlFor="required" className="font-normal cursor-pointer">
                    Required
                  </Label>
                </div>
              </div>
            </RadioGroup>
            {anchorRequirementError && <p className="text-sm text-red-600 mt-1">Please select Anchor Text Required option</p>}
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">Metrics</h2>
            <p className="text-sm text-muted-foreground mt-1">Define quality metrics for each link</p>
          </div>

          <Card className="overflow-hidden">
            <div className="grid grid-cols-4 gap-4 p-4 bg-muted/50 border-b font-medium text-sm">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <span>DA <span className="text-red-500">*</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="flexible-da"
                      checked={flexiblePerMetric.da}
                      onCheckedChange={(checked) => {
                        const value = checked === true
                        setFlexiblePerMetric((prev) => {
                          const next = { ...prev, da: value }
                          if (!value && Object.values(next).every((v) => !v)) setFlexibleOnMetrics("no")
                          return next
                        })
                        if (value) setFlexibleOnMetrics("yes")
                      }}
                    />
                    <Label htmlFor="flexible-da" className="text-xs font-normal cursor-pointer">Flexible</Label>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-normal">minimum</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span>DR <span className="text-red-500">*</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="flexible-dr"
                      checked={flexiblePerMetric.dr}
                      onCheckedChange={(checked) => {
                        const value = checked === true
                        setFlexiblePerMetric((prev) => {
                          const next = { ...prev, dr: value }
                          if (!value && Object.values(next).every((v) => !v)) setFlexibleOnMetrics("no")
                          return next
                        })
                        if (value) setFlexibleOnMetrics("yes")
                      }}
                    />
                    <Label htmlFor="flexible-dr" className="text-xs font-normal cursor-pointer">Flexible</Label>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-normal">minimum</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>Traffic <span className="text-red-500">*</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="flexible-traffic"
                      checked={flexiblePerMetric.traffic}
                      onCheckedChange={(checked) => {
                        const value = checked === true
                        setFlexiblePerMetric((prev) => {
                          const next = { ...prev, traffic: value }
                          if (!value && Object.values(next).every((v) => !v)) setFlexibleOnMetrics("no")
                          return next
                        })
                        if (value) setFlexibleOnMetrics("yes")
                      }}
                    />
                    <Label htmlFor="flexible-traffic" className="text-xs font-normal cursor-pointer">Flexible</Label>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-normal">minimum</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <span>Spam <span className="text-red-500">*</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="flexible-spam"
                      checked={flexiblePerMetric.spam}
                      onCheckedChange={(checked) => {
                        const value = checked === true
                        setFlexiblePerMetric((prev) => {
                          const next = { ...prev, spam: value }
                          if (!value && Object.values(next).every((v) => !v)) setFlexibleOnMetrics("no")
                          return next
                        })
                        if (value) setFlexibleOnMetrics("yes")
                      }}
                    />
                    <Label htmlFor="flexible-spam" className="text-xs font-normal cursor-pointer">Flexible</Label>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-normal">minimum</span>
              </div>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Input
                    type="number"
                    value={globalMetrics.da}
                    onChange={(e) => setGlobalMetrics({ ...globalMetrics, da: e.target.value })}
                    placeholder="50"
                    className="h-9"
                  />
                </div>

                <div>
                  <Input
                    type="number"
                    value={globalMetrics.dr}
                    onChange={(e) => setGlobalMetrics({ ...globalMetrics, dr: e.target.value })}
                    placeholder="45"
                    className="h-9"
                  />
                </div>

                <div>
                  <Input
                    type="number"
                    value={globalMetrics.traffic}
                    onChange={(e) => setGlobalMetrics({ ...globalMetrics, traffic: e.target.value })}
                    placeholder="10000"
                    className="h-9"
                  />
                </div>

                <div>
                  <Input
                    type="number"
                    value={globalMetrics.spam}
                    onChange={(e) => setGlobalMetrics({ ...globalMetrics, spam: e.target.value })}
                    placeholder="5"
                    className="h-9"
                  />
                </div>
              </div>
            </div>
          </Card>

          <div className="mt-6 pt-6 border-t" ref={flexibleOnMetricsRef}>
            <Label className="text-base font-medium mb-3 block">
              Flexible on Metrics? <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={flexibleOnMetrics || ""}
              onValueChange={(value) => {
                setFlexibleOnMetrics(value as "yes" | "no")
                if (value === "no") {
                  setFlexiblePerMetric({ da: false, dr: false, traffic: false, spam: false })
                }
                if (flexibleOnMetricsError) {
                  setFlexibleOnMetricsError(false)
                }
              }}
              className={flexibleOnMetricsError ? "border-red-500" : ""}
            >
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="flexible-yes" />
                  <Label htmlFor="flexible-yes" className="font-normal cursor-pointer">
                    Yes - opportunities can go outside of metrics if needed
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="flexible-no" />
                  <Label htmlFor="flexible-no" className="font-normal cursor-pointer">
                    No - opportunities cannot go outside of metrics
                  </Label>
                </div>
              </div>
            </RadioGroup>
            {flexibleOnMetricsError && <p className="text-sm text-red-600 mt-1">Please select Flexible on Metrics option</p>}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Opportunity Requirements</h2>

          <div className="space-y-4 mb-6 pb-6 border-b" ref={opportunityTypesRef}>
            <div>
              <Label className="text-base font-medium">
                What type of opportunities can be in the order? <span className="text-red-500">*</span>
              </Label>
            </div>

            <div className={`space-y-3 ${opportunityTypesError ? "border-2 border-red-500 rounded-md p-3 bg-red-50" : ""}`}>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="generalDonation"
                  checked={opportunityTypes.generalDonation}
                  onChange={() => handleOpportunityTypeChange("generalDonation")}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="generalDonation" className="font-normal cursor-pointer">
                  General Donation
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="event"
                  checked={opportunityTypes.event}
                  onChange={() => handleOpportunityTypeChange("event")}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="event" className="font-normal cursor-pointer">
                  Event
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="organizationMemberships"
                  checked={opportunityTypes.organizationMemberships}
                  onChange={() => handleOpportunityTypeChange("organizationMemberships")}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="organizationMemberships" className="font-normal cursor-pointer">
                  Organization Memberships
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="chamberOfCommerceMemberships"
                  checked={opportunityTypes.chamberOfCommerceMemberships}
                  onChange={() => handleOpportunityTypeChange("chamberOfCommerceMemberships")}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="chamberOfCommerceMemberships" className="font-normal cursor-pointer">
                  Chamber of Commerce Memberships
                </Label>
              </div>

              <div className="flex items-center space-x-2 pt-2 border-t">
                <input
                  type="checkbox"
                  id="selectAllOpportunityTypes"
                  checked={Object.values(opportunityTypes).every((value) => value)}
                  onChange={handleSelectAllOpportunityTypes}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="selectAllOpportunityTypes" className="font-normal cursor-pointer font-medium">
                  Select All
                </Label>
              </div>
            </div>
            {opportunityTypesError && <p className="text-sm text-red-600 mt-1">Please select at least one opportunity type</p>}
          </div>

          {/* Wants Section */}
          <div className={`space-y-4 mb-6 ${wantsError ? "border-2 border-red-500 rounded-md p-3 bg-red-50" : ""}`} ref={wantsRef}>
            <div>
              <Label className="text-base font-medium">Wants (Preferred outlets/topics) <span className="text-red-500">*</span></Label>
              <p className="text-sm text-muted-foreground">
                Prepopulate from Client/Business record, allow editing specific to the order
              </p>
            </div>

            <div className="relative">
              <Input
                placeholder="Search for categories to add..."
                value={wantsSearchTerm}
                onChange={(e) => {
                  setWantsSearchTerm(e.target.value)
                  setShowWantsDropdown(true)
                  if (!e.target.value.trim()) {
                    setShowWantsDropdown(true)
                  }
                }}
                onFocus={() => setShowWantsDropdown(true)}
                onBlur={(e) => {
                  const relatedTarget = e.relatedTarget as HTMLElement
                  if (
                    relatedTarget &&
                    (relatedTarget.closest(".wants-dropdown") || relatedTarget.closest('button[type="button"]'))
                  ) {
                    return
                  }
                  setTimeout(() => setShowWantsDropdown(false), 200)
                }}
                className="pr-8"
              />
              {wantsSearchTerm && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setWantsSearchTerm("")
                    setShowWantsDropdown(true)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded p-0.5 z-10"
                >
                  <X className="h-3 w-3" />
                </button>
              )}

              {showWantsDropdown && (
                <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto wants-dropdown">
                  {wantsSearchTerm.trim().length > 0 && wantsSearchTerm.trim().length < 2 ? (
                    <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                  ) : loadingCategories && (!categoriesLoaded || wantsSearchTerm.trim().length >= 2) ? (
                    <div className="p-3 text-sm text-gray-600">Searching categories...</div>
                  ) : categories.length === 0 ? (
                    <div className="p-3 text-sm text-gray-600">
                      {wantsSearchTerm.trim()
                        ? `No categories found for "${wantsSearchTerm}"`
                        : "No categories available"}
                    </div>
                  ) : (
                    <div>
                      {/* All Categories option - FIRST OPTION */}
                        <div
                        className={`px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm font-medium border-b border-gray-200 ${
                          isAllCategoriesSelected 
                            ? 'bg-blue-50 text-blue-700' 
                            : 'text-gray-900 bg-white'
                        } ${loadingAllCategories ? 'opacity-50 cursor-wait' : ''}`}
                        onClick={() => {
                          if (!loadingAllCategories) {
                            addAllWantCategories()
                          }
                        }}
                        >
                        {loadingAllCategories ? 'Loading all categories...' : 'All Categories'}
                        </div>
                      {/* Show individual categories */}
                      {(wantsSearchTerm.trim() === "" ? initialCategories : categories)
                        .filter(
                          (cat) => !isAllCategoriesSelected && !wantsList.some((w) => w.id === cat.id) && !avoidList.some((a) => a.id === cat.id),
                        )
                        .map((category) => (
                          <div
                            key={category.id}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => addWantCategory(category)}
                          >
                            {category.text}
                          </div>
                        ))}

                      {(wantsSearchTerm.trim() === "" ? initialCategories.length === 20 : categories.length === 20) && (
                        <div className="p-2 text-xs text-muted-foreground border-t bg-gray-50">
                          Type a category name to search for more results
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {(wantsList.length > 0 || isAllCategoriesSelected) && (
              <div className="flex flex-wrap gap-2">
                {isAllCategoriesSelected && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    All Categories
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={removeAllCategories} 
                    />
                  </Badge>
                )}
                {wantsList.map((category) => (
                  <Badge key={category.id} variant="secondary" className="flex items-center gap-1">
                    {category.text}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeWantCategory(category.id)} />
                  </Badge>
                ))}
              </div>
            )}
            {wantsError && <p className="text-sm text-red-600 mt-1">Please add at least one category to Wants</p>}
          </div>

          {/* Avoid Section */}
          <div className={`space-y-4 ${avoidError ? "border-2 border-red-500 rounded-md p-3 bg-red-50" : ""}`} ref={avoidRef}>
            <div>
              <Label>
                Avoid (Outlets/topics to avoid) <span className="text-red-500">*</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Prepopulate from Client/Business record, allow editing specific to the order
              </p>
            </div>

            <div className="relative">
              <Input
                placeholder="Search for categories to avoid..."
                value={avoidSearchTerm}
                onChange={(e) => {
                  setAvoidSearchTerm(e.target.value)
                  setShowAvoidDropdown(true)
                  if (!e.target.value.trim()) {
                    setShowAvoidDropdown(true)
                  }
                }}
                onFocus={() => setShowAvoidDropdown(true)}
                onBlur={(e) => {
                  const relatedTarget = e.relatedTarget as HTMLElement
                  if (
                    relatedTarget &&
                    (relatedTarget.closest(".avoid-dropdown") || relatedTarget.closest('button[type="button"]'))
                  ) {
                    return
                  }
                  setTimeout(() => setShowAvoidDropdown(false), 200)
                }}
                className="pr-8"
              />
              {avoidSearchTerm && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setAvoidSearchTerm("")
                    setShowAvoidDropdown(true)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded p-0.5 z-10"
                >
                  <X className="h-3 w-3" />
                </button>
              )}

              {showAvoidDropdown && (
                <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto avoid-dropdown">
                  {avoidSearchTerm.trim().length > 0 && avoidSearchTerm.trim().length < 2 ? (
                    <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                  ) : loadingCategories && (!categoriesLoaded || avoidSearchTerm.trim().length >= 2) ? (
                    <div className="p-3 text-sm text-gray-600">Searching categories...</div>
                  ) : categories.length === 0 ? (
                    <div className="p-3 text-sm text-gray-600">
                      {avoidSearchTerm.trim()
                        ? `No categories found for "${avoidSearchTerm}"`
                        : "No categories available"}
                    </div>
                  ) : (
                    <div>
                      {(avoidSearchTerm.trim() === "" ? initialCategories : categories)
                        .filter(
                          (cat) => !avoidList.some((a) => a.id === cat.id) && !wantsList.some((w) => w.id === cat.id),
                        )
                        .map((category) => (
                          <div
                            key={category.id}
                            className="px-4 py-3 hover:bg-red-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => addAvoidCategory(category)}
                          >
                            {category.text}
                          </div>
                        ))}

                      {(avoidSearchTerm.trim() === "" ? initialCategories.length === 20 : categories.length === 20) && (
                        <div className="p-2 text-xs text-muted-foreground border-t bg-gray-50">
                          Type a category name to search for more results
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {avoidList.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {avoidList.map((category) => (
                  <Badge key={category.id} variant="secondary" className="flex items-center gap-1">
                    {category.text}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeAvoidCategory(category.id)} />
                  </Badge>
                ))}
              </div>
            )}
            {avoidError && <p className="text-sm text-red-600 mt-1">Please add at least one category to Avoids</p>}
          </div>

          <div className="mt-6 pt-6 border-t" ref={flexibleOnWantsAvoidsRef}>
            <Label className="text-base font-medium mb-3 block">
              Flexible on Wants/Avoids? <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={flexibleOnWantsAvoids || ""}
              onValueChange={(value) => {
                setFlexibleOnWantsAvoids(value as "yes" | "no")
                if (flexibleOnWantsAvoidsError) {
                  setFlexibleOnWantsAvoidsError(false)
                }
              }}
              className={flexibleOnWantsAvoidsError ? "border-red-500" : ""}
            >
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="flexible-wants-yes" />
                  <Label htmlFor="flexible-wants-yes" className="font-normal cursor-pointer">
                    Yes - opportunities can go outside of categories if needed
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="flexible-wants-no" />
                  <Label htmlFor="flexible-wants-no" className="font-normal cursor-pointer">
                    No - opportunities cannot go outside of categories
                  </Label>
                </div>
                </div>
              </RadioGroup>
              {flexibleOnWantsAvoidsError && <p className="text-sm text-red-600 mt-1">Please select Flexible on Wants/Avoids option</p>}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Other Order Instructions</h2>
          <div>
            <Label htmlFor="otherRequirements">Notes for Team</Label>
            <Textarea
              id="otherRequirements"
              value={otherRequirements}
              onChange={(e) => setOtherRequirements(e.target.value)}
              placeholder="Enter any additional notes or requirements for the team..."
              rows={6}
            />
          </div>
        </Card>

        <div className="flex gap-4 justify-end">
          {asModal && onClose && (
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          {!asModal && (
            <Button type="button" variant="outline" onClick={() => router?.back()} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button 
            type="submit" 
            disabled={isSubmitting}
            onMouseDown={(e) => {
              // Prevent button from receiving focus to avoid scroll to bottom
              e.preventDefault()
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Order...
              </>
            ) : (
              "Create Order"
            )}
          </Button>
        </div>
      </form>
    </>
  )

  if (asModal) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose && onClose()}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Order</DialogTitle>
            <DialogDescription>Fill out the order details</DialogDescription>
          </DialogHeader>
          <div className="container mx-auto py-4 px-4 max-w-7xl">
            {formContent}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      {formContent}
    </div>
  )
}

function CreateOrderFormWithSearchParams(props: Omit<CreateOrderFormProps, 'asModal' | 'initialClientId'> & { initialClientId?: string }) {
  const searchParams = useSearchParams()
  const clientIdParam = searchParams.get("clientId") || ""
  
  return <CreateOrderFormContent {...props} initialClientId={clientIdParam} asModal={false} />
}

export function CreateOrderForm(props: CreateOrderFormProps) {
  if (props.asModal) {
    return <CreateOrderFormContent {...props} />
  }
  
  return (
    <Suspense fallback={<div className="container mx-auto py-8">Loading...</div>}>
      <CreateOrderFormWithSearchParams 
        isOpen={props.isOpen}
        onClose={props.onClose}
        onSave={props.onSave}
        initialClientId={props.initialClientId}
      />
    </Suspense>
  )
}
